import { Link } from "react-router-dom";
import Reveal from "@/components/Reveal";
import WordReveal from "@/components/WordReveal";
import { useSeo, SITE_URL } from "@/lib/seo";
import { breadcrumb } from "@/lib/structuredData";
import { TRADE_FAIRS } from "@/data/tradeFairs";

export const TradeFairs = () => {
  useSeo({
    title: "International Trade Fairs — India Sourcing Buyer Guide | Asia Sourcing",
    description:
      "Independent buyer guides to major international furniture, lighting and home-décor trade fairs — IHGF Delhi, imm cologne, Maison&Objet, High Point Market, Las Vegas Market. Meet the Asia Sourcing team.",
    path: "/trade-fairs",
    type: "article",
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "CollectionPage",
          "@id": `${SITE_URL}/trade-fairs#collection`,
          name: "International Trade Fairs — India Sourcing Buyer Guide",
          url: `${SITE_URL}/trade-fairs`,
          hasPart: TRADE_FAIRS.map((f) => ({
            "@type": "Article",
            "@id": `${SITE_URL}/trade-fairs/${f.slug}#article`,
            headline: f.h1,
            url: `${SITE_URL}/trade-fairs/${f.slug}`,
          })),
        },
        breadcrumb([
          ["Home", SITE_URL],
          ["Trade fairs", `${SITE_URL}/trade-fairs`],
        ]),
      ],
    },
  });

  return (
    <article data-testid="trade-fairs-index">
      <section
        className="wrap"
        style={{ paddingBlock: "clamp(3.5rem, 6vw, 5.5rem)" }}
      >
        <Reveal as="p" className="eyebrow">Buyer guide · International trade fairs</Reveal>
        <WordReveal
          as="h1"
          className="display"
          style={{
            fontSize: "clamp(2rem, 4vw, 3.4rem)",
            margin: "0 0 1.4rem",
            letterSpacing: "-.015em",
            lineHeight: 1.05,
          }}
          text="International Furniture, Lighting & Home Décor Trade Fairs."
        />
        <Reveal
          as="p"
          style={{
            color: "var(--ink-soft)",
            fontSize: "1.08rem",
            lineHeight: 1.7,
            maxWidth: "72ch",
            margin: "0 0 2rem",
          }}
        >
          Asia Sourcing's team attends leading international trade fairs — IHGF Delhi, imm
          cologne, Maison&Objet Paris, High Point Market and Las Vegas Market — and meets
          international buyers to discuss furniture, lighting, home décor and India sourcing
          opportunities. These pages are independent buyer guides. For official registration,
          exhibitor listings and current-edition dates always visit each event's official website.
        </Reveal>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
            gap: "1rem",
          }}
        >
          {TRADE_FAIRS.map((fair) => (
            <Link
              key={fair.slug}
              to={`/trade-fairs/${fair.slug}`}
              data-testid={`trade-fair-card-${fair.slug}`}
              style={{
                display: "block",
                padding: "1.4rem 1.5rem",
                border: "1px solid var(--line)",
                color: "var(--ink)",
                textDecoration: "none",
                background: "var(--paper)",
                transition: "border-color 200ms",
              }}
            >
              <p
                className="mono"
                style={{
                  color: "var(--brass-deep)",
                  fontSize: ".62rem",
                  letterSpacing: ".14em",
                  textTransform: "uppercase",
                  margin: "0 0 .6rem",
                }}
              >
                {fair.city} · {fair.country}
              </p>
              <p
                style={{
                  fontFamily: "var(--display)",
                  fontSize: "1.24rem",
                  letterSpacing: "-.005em",
                  margin: "0 0 .6rem",
                  lineHeight: 1.3,
                }}
              >
                {fair.name} →
              </p>
              <p
                style={{
                  color: "var(--ink-soft)",
                  fontSize: ".92rem",
                  lineHeight: 1.6,
                  margin: "0 0 .5rem",
                }}
              >
                {fair.cadence}
              </p>
              <p
                style={{
                  color: "var(--ink-soft)",
                  fontSize: ".85rem",
                  lineHeight: 1.55,
                  margin: 0,
                }}
              >
                Categories: {fair.categories.slice(0, 4).join(" · ")}
              </p>
            </Link>
          ))}
        </div>
      </section>
    </article>
  );
};

export default TradeFairs;
